/**
 * Car-body presets — the per-body geometry that turns "a car" into an Octane,
 * a Dominus or a Batmobile.
 *
 * SOURCE. Everything in this file is transcribed verbatim from RocketSim
 * `src/Sim/Car/CarConfig/CarConfig.cpp` (github.com/ZealanL/RocketSim, main),
 * which is the only place any of these numbers is published as a complete set.
 * It is a `[1-src]` block in the sense of ./constants' sourcing rule: RLUtilities
 * models the Octane only, the RLBot wiki publishes a partial hitbox table that
 * disagrees with RocketSim's by a flat 2.5 uu on every axis (see the conflict
 * note on OCTANE_HITBOX in ./constants), and no source at all publishes wheel
 * offsets or suspension rest lengths for the other six bodies.
 *
 * WHY THESE ARE NOT `GetLocalCollisionExtent()`. RocketSim's own comment on the
 * table, quoted because it is the entire justification for preferring these to
 * the community numbers:
 *
 *     "They are all wrong. The values that Rocket League gives you when you
 *      call GetLocalCollisionExtent() are slightly larger than the actual
 *      values used in simulation... The hitbox values I have produce a matching
 *      inertia matrix with real RL"
 *
 * That claim is checkable for the Octane and it checks out: the box below
 * reproduces RLUtilities' independently hard-coded inertia tensor
 * diag(751, 1334, 1836) to 0.1 %, while the community box does not (it gives
 * 699.75 on the roll axis). See OCTANE_INERTIA_PER_MASS below.
 *
 * HITBOX SIZES ARE FULL EXTENTS, not half-extents. Every consumer in this tree
 * halves them exactly once; `./obb` is the only place that should.
 *
 * COORDINATE CAVEAT. RocketSim's wheel-offset y is the car's RIGHT in its own
 * naming and our +Y column is `left`. The component VALUES are identical either
 * way (docs/PHYSICS_REFERENCE.md §6) and every wheel set here is mirror-
 * symmetric in Y, so the naming only matters when reading debug output. The
 * stored `connectionOffset.y` is always POSITIVE and is negated for the mirrored
 * wheel of the pair — that is RocketSim's own storage convention, and
 * ./suspension is what expands a pair into two wheels.
 */

import type { Vec3 } from './math';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The seven bodies RocketSim ships, in its own enum order (OCTANE = 0 ..
 * PSYCLOPS = 6). The order is load-bearing only in that it is how the source
 * tables are indexed; nothing here depends on it at runtime.
 *
 * NOT from RocketSim: the string names. RocketSim identifies a body by its
 * index into seven parallel arrays, which is not something that survives a JSON
 * round trip legibly, so a car carries a name instead. The names are the
 * lowercased enum identifiers, one-for-one.
 */
export type CarBody =
  | 'octane'
  | 'dominus'
  | 'plank'
  | 'breakout'
  | 'hybrid'
  | 'merc'
  | 'psyclops';

/** Every body, in RocketSim's enum order. */
export const CAR_BODIES: readonly CarBody[] = Object.freeze([
  'octane',
  'dominus',
  'plank',
  'breakout',
  'hybrid',
  'merc',
  'psyclops',
] as const);

/** One axle: both wheels of a pair share everything but the sign of y. */
export interface WheelPairConfig {
  /** Wheel radius, uu. [1-src: RS `FRONT_WHEEL_RADS` / `BACK_WHEEL_RADS`] */
  readonly radius: number;
  /**
   * Fully extended suspension length, uu. RocketSim subtracts
   * `SUSPENSION.maxTravel` from this before handing it to Bullet, so the
   * spring's zero-force length is 12 uu shorter than this number — see the
   * header of ./suspension.
   * [1-src: RS `FRONT_WHEEL_SUS_REST` / `BACK_WHEEL_SUS_REST`]
   */
  readonly suspensionRestLength: number;
  /**
   * Attachment point in the car's local frame, for the +Y wheel of the pair.
   * Mirror y for the other side.
   * [1-src: RS `FRONT_WHEELS_OFFSET` / `BACK_WHEELS_OFFSET`]
   */
  readonly connectionOffset: Readonly<Vec3>;
}

/**
 * One car body. Field-for-field RocketSim's `CarConfig`, plus `body` (see the
 * note on `CarBody`).
 */
export interface CarConfig {
  /** Which preset this is. Ours, not RocketSim's — see `CarBody`. */
  readonly body: CarBody;
  /**
   * Collision box, FULL extents in the car's local frame:
   * x = length (nose to tail), y = width, z = height.
   * [1-src: RS `HITBOX_SIZES`]
   */
  readonly hitboxSize: Readonly<Vec3>;
  /**
   * Box centre offset from the car's origin, in the car's local frame. The
   * origin is the centre of MASS and the box is not centred on it.
   * [1-src: RS `HITBOX_OFFSETS`]
   */
  readonly hitboxOffset: Readonly<Vec3>;
  readonly frontWheels: WheelPairConfig;
  readonly backWheels: WheelPairConfig;
  /**
   * True when the body's front two wheels are visually combined into one.
   * PSYCLOPS is the only such body. RocketSim's own comment is explicit that
   * this changes nothing about the simulation — "no car has only 3 simulated
   * wheels" — so ./suspension still casts four rays for it. Carried because it
   * is part of the source struct and a renderer will want it.
   * [1-src: RS `CarConfig::threeWheels`]
   */
  readonly threeWheels: boolean;
  /**
   * A flip is triggered instead of a double jump when
   * |pitch| + |yaw| + |roll| >= this — the SUM of absolute values, not a vector
   * magnitude. Per-body in RocketSim's struct; 0.5 for every shipped body.
   * [confirmed: RS `CarConfig::dodgeDeadzone`, RU `Dodge::input_threshold`]
   */
  readonly dodgeDeadzone: number;
}

/** Moment of inertia as I / m (uu^2) about the car's local axes. */
export interface InertiaPerMass {
  /** About local x — forward, i.e. roll. */
  readonly roll: number;
  /** About local y — left, i.e. pitch. */
  readonly pitch: number;
  /** About local z — up, i.e. yaw. */
  readonly yaw: number;
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

const v = (x: number, y: number, z: number): Readonly<Vec3> => Object.freeze({ x, y, z });

const axle = (
  radius: number,
  suspensionRestLength: number,
  x: number,
  y: number,
  z: number
): WheelPairConfig =>
  Object.freeze({ radius, suspensionRestLength, connectionOffset: v(x, y, z) });

/** Every shipped body has the same dodge deadzone. [RS `dodgeDeadzone = 0.5`] */
const DODGE_DEADZONE_ALL = 0.5;

const config = (
  body: CarBody,
  hitboxSize: Readonly<Vec3>,
  hitboxOffset: Readonly<Vec3>,
  frontWheels: WheelPairConfig,
  backWheels: WheelPairConfig,
  threeWheels = false
): CarConfig =>
  Object.freeze({
    body,
    hitboxSize,
    hitboxOffset,
    frontWheels,
    backWheels,
    threeWheels,
    dodgeDeadzone: DODGE_DEADZONE_ALL,
  });

/**
 * The default body, and the only one any source other than RocketSim describes.
 * Every constant in ./constants that used to hard-code a car's geometry now
 * reads it from here.
 */
export const OCTANE_CONFIG: CarConfig = config(
  'octane',
  v(120.507, 86.6994, 38.6591),
  v(13.8757, 0, 20.755),
  axle(12.5, 38.755, 51.25, 25.9, 20.755),
  axle(15.0, 37.055, -33.75, 29.5, 20.755)
);

export const DOMINUS_CONFIG: CarConfig = config(
  'dominus',
  v(130.427, 85.7799, 33.8),
  v(9, 0, 15.75),
  axle(12.0, 33.95, 50.3, 31.1, 15.75),
  axle(13.5, 33.85, -34.75, 33.0, 15.75)
);

/** The flat, wide bodies — Batmobile and friends. */
export const PLANK_CONFIG: CarConfig = config(
  'plank',
  v(131.32, 87.1704, 31.8944),
  v(9.00857, 0, 12.0942),
  axle(12.5, 31.9242, 49.97, 27.8, 12.0942),
  axle(17.0, 27.9242, -35.43, 20.28, 12.0942)
);

export const BREAKOUT_CONFIG: CarConfig = config(
  'breakout',
  v(133.992, 83.021, 32.8),
  v(12.5, 0, 11.75),
  axle(13.5, 29.7, 51.5, 26.67, 11.75),
  axle(15.0, 29.666, -35.75, 35.0, 11.75)
);

export const HYBRID_CONFIG: CarConfig = config(
  'hybrid',
  v(129.519, 84.6879, 36.6591),
  v(13.8757, 0, 20.755),
  axle(12.5, 38.755, 51.25, 25.9, 20.755),
  axle(15.0, 37.055, -34.0, 29.5, 20.755)
);

export const MERC_CONFIG: CarConfig = config(
  'merc',
  v(123.22, 79.2103, 44.1591),
  v(11.3757, 0, 21.505),
  axle(15.0, 39.505, 51.25, 25.9, 21.505),
  axle(15.0, 39.105, -33.75, 29.5, 21.505)
);

/**
 * The only `threeWheels` body: its front two wheels are combined visually, and
 * RocketSim still simulates four. Its hitbox is the Octane's plus exactly 0.134
 * on every axis, which is worth knowing only so that nobody "corrects" it to
 * the Octane's.
 */
export const PSYCLOPS_CONFIG: CarConfig = config(
  'psyclops',
  v(120.641, 86.8334, 38.7931),
  v(13.8757, 0, 15.0),
  axle(12.5, 33.0, 51.25, 5.0, 15.0),
  axle(15.0, 31.3, -33.75, 29.5, 15.0),
  true
);

/** Every preset, by name. */
export const CAR_CONFIGS: Readonly<Record<CarBody, CarConfig>> = Object.freeze({
  octane: OCTANE_CONFIG,
  dominus: DOMINUS_CONFIG,
  plank: PLANK_CONFIG,
  breakout: BREAKOUT_CONFIG,
  hybrid: HYBRID_CONFIG,
  merc: MERC_CONFIG,
  psyclops: PSYCLOPS_CONFIG,
});

/**
 * Look a preset up by name. Throws on an unknown name rather than falling back
 * to the Octane: a silently substituted body is a car that drives differently
 * from the one that was asked for, which is exactly the bug this whole module
 * exists to make impossible.
 */
export const carConfigFor = (body: CarBody): CarConfig => {
  const found = CAR_CONFIGS[body];
  if (found === undefined) throw new Error(`unknown car body ${String(body)}`);
  return found;
};

// ---------------------------------------------------------------------------
// Inertia
// ---------------------------------------------------------------------------

/**
 * `btBoxShape::calculateLocalInertia`, divided through by mass, for a box given
 * as FULL extents. Bullet's own expression, verbatim:
 *
 *     lx = 2*halfExtents.x; ly = 2*halfExtents.y; lz = 2*halfExtents.z;
 *     inertia = mass/12 * (ly*ly + lz*lz,  lx*lx + lz*lz,  lx*lx + ly*ly)
 *
 * so this takes the FULL extents directly and never halves anything. It is the
 * standard uniform-density rectangular cuboid tensor; RocketSim gets a car's
 * inertia from exactly this call on `HITBOX_SIZES[body]`.
 */
export const boxInertiaPerMass = (fullExtents: Readonly<Vec3>): InertiaPerMass => {
  const { x, y, z } = fullExtents;
  return {
    roll: (y * y + z * z) / 12,
    pitch: (x * x + z * z) / 12,
    yaw: (x * x + y * y) / 12,
  };
};

/**
 * The Octane's tensor, and the one the whole simulation was built and validated
 * against. Exported by ./constants as `CAR_INERTIA_PER_MASS`.
 *
 * [confirmed: RU hard-codes `I = m * diag(751, 1334, 1836)`; RS derives
 *  diag(750.943, 1334.705, 1836.560) from OCTANE_CONFIG.hitboxSize via
 *  btBoxShape::calculateLocalInertia — agreement to 0.1 %]
 * [conflict: RU's tensor is NOT derivable from RU's OWN hitbox (that box gives
 *  699.75 on the roll axis), so RU's tensor is an empirical fit while RS's is
 *  geometric. Structurally incompatible sources that happen to agree — see
 *  docs/CONSTANTS_SOURCES.md.]
 *
 * WHY THIS IS A LITERAL AND NOT `boxInertiaPerMass(OCTANE_CONFIG.hitboxSize)`.
 * The two disagree in the fourth significant figure, and this is the value the
 * shipped game has always used: substituting the geometric one would change
 * every grounded car's pitch and roll response by ~0.008 % and so change the
 * result of every existing trajectory test by more than zero. The rounded
 * tensor is genuinely better sourced than the derived one for this body — it is
 * the only tensor any source states outright — so the Octane keeps it and the
 * six bodies no source has an opinion on get the geometric derivation, which is
 * what RocketSim itself would compute for them.
 */
export const OCTANE_INERTIA_PER_MASS: InertiaPerMass = Object.freeze({
  roll: 751,
  pitch: 1334,
  yaw: 1836,
});

/**
 * Tensors a source states outright, which therefore beat the derivation. Only
 * the Octane has one; see OCTANE_INERTIA_PER_MASS.
 */
const SOURCED_INERTIA: Partial<Record<CarBody, InertiaPerMass>> = Object.freeze({
  octane: OCTANE_INERTIA_PER_MASS,
});

const inertiaCache = new WeakMap<CarConfig, InertiaPerMass>();

/**
 * A body's inertia tensor, as I / m. Memoized per config object, because the
 * per-tick paths (./suspension, ./car-arena) read it every tick per car and the
 * presets are frozen singletons.
 */
export const carInertiaPerMass = (config: CarConfig): InertiaPerMass => {
  const cached = inertiaCache.get(config);
  if (cached !== undefined) return cached;
  const derived =
    SOURCED_INERTIA[config.body] ?? Object.freeze(boxInertiaPerMass(config.hitboxSize));
  inertiaCache.set(config, derived);
  return derived;
};
